/**
 * L1: the shell. It owns the single AppState, implements AppActions by calling the lane modules,
 * and recomposes the screens. Rendering is plain DOM: each change rebuilds the visible screen
 * from state, keeping the focused input (data-keep), its caret, and each screen's scroll position.
 *
 * Security posture wired here (03 CSO caveats):
 * - nothing loads until unlock, and lock drops the snapshot, reveals and Large Type;
 * - every tap passes the clipboard guard first (copy, then clear on the next tap);
 * - page hide drops reveals, raises the privacy cover, and applies auto-lock.
 *
 * Added from the CSO review of PR #95 (J-3705 prerequisites):
 * - the wiring fails closed: the data source and the unlocker are required, with no default to
 *   fall back on, and the placeholder unlocker is refused in front of anything but fixture data;
 * - the data source loads with the unlock's session, every lock path revokes it, and a load that
 *   finishes after a lock (or for an earlier session) is discarded;
 * - the URL carries opaque per-session item tokens, never an item id (router.ts).
 *
 * Added from the CSO gate on PR #100: every Face ID prompt has an id, an answer counts only for
 * the prompt that is open now, and a prompt closed before its answer (a hide under "Immediately",
 * a lock) has its AbortSignal aborted.
 */
import {
  splitFieldRef,
  systemClock,
  type AppActions,
  type AppState,
  type AutoLockSetting,
  type ClipboardPort,
  type Clock,
  type FieldRef,
  type HomePrefs,
  type Route,
  type Sheet,
  type SortKey,
  type Unlocker,
  type UnlockResult,
  type UnlockSession,
  type VaultDataSource,
  type WalletId,
} from "../contract.js";
import { revokeSession } from "../auth/session.js";
import { ClipboardGuard, isCopyControl } from "../detail/clipboard.js";
import { nextExpiry, pruneExpired, revealFor, toggleReveal } from "../detail/reveal.js";
import { beginUnlock, finishUnlock, initialLock, lockNow, onHidden, onVisible, type LockState } from "../lock/session.js";
import { watchVisibility } from "../lock/privacy.js";
import { caretAfterRewrite, toggleWalletInText } from "../search/query.js";
import { DEFAULT_HOME_PREFS } from "../vm/home.js";
import { copyValue } from "../vm/detail.js";
import { renderClipboardBar, renderToast } from "../views/clipboard-bar.js";
import { renderDetail, renderFieldMenuSheet, renderItemMenuSheet } from "../views/detail.js";
import { renderCustomizeHomeSheet, renderHome } from "../views/home.js";
import { renderCategoriesSheet, renderItems, renderSortSheet } from "../views/items.js";
import { renderLargeType } from "../views/large-type.js";
import { renderLock, renderPrivacyCover } from "../views/lock.js";
import { renderSearch, renderSearchScopeSheet } from "../views/search.js";
import { renderAutoLockSheet, renderSettings } from "../views/settings.js";
import { renderSwitcherSheet } from "../views/switcher.js";
import { emptyState } from "./chrome.js";
import { h, icon } from "./dom.js";
import { formatRoute, ItemTokens, parseHash, tabOf, type TabId } from "./router.js";

export interface AppOptions {
  version: string;
  /** Required: there is no default source. Each build's entry (src/entry/) chooses it. */
  dataSource: VaultDataSource;
  /** Required: there is no default unlocker. Each build's entry (src/entry/) chooses it. */
  unlocker: Unlocker;
  /** Fields pinned to Home at start. */
  pinned?: readonly FieldRef[];
  clipboard?: ClipboardPort;
  clock?: Clock;
}

/**
 * Refuses a wiring that could open real data without a real unlock. The placeholder unlocker
 * answers "ok" without checking anything, so it may only ever sit in front of fixture data.
 * A missing source or unlocker (a plain-JS caller) is refused rather than defaulted.
 */
export function assertSafeWiring(dataSource: VaultDataSource | undefined, unlocker: Unlocker | undefined): void {
  if (!dataSource) throw new Error("startApp: no data source wired in; refusing to start.");
  if (!unlocker) throw new Error("startApp: no unlocker wired in; refusing to start.");
  if (unlocker.method === "placeholder" && dataSource.kind !== "fixture") {
    throw new Error(`startApp: the placeholder unlocker may only open fixture data, not a ${dataSource.kind} source; refusing to start.`);
  }
}

const TOAST_MS = 1_600;

function screenKey(r: Route): string {
  return r.name === "item" ? `item:${r.itemId}` : r.name === "items" ? `items:${r.list ?? "all"}` : r.name;
}

function parentOf(r: Route): Route {
  if (r.name === "item") return { name: "items", list: "all" };
  if (r.name === "items" && r.list && r.list !== "all") return { name: "items", list: "all" };
  return { name: "home" };
}

export function startApp(root: HTMLElement, opts: AppOptions): void {
  assertSafeWiring(opts.dataSource, opts.unlocker);
  const clock = opts.clock ?? systemClock;
  const source = opts.dataSource;
  const unlocker = opts.unlocker;
  const port: ClipboardPort = opts.clipboard ?? { writeText: (t) => navigator.clipboard.writeText(t) };

  let lock: LockState = initialLock();
  /** The live unlock session while unlocked, else null. Revoked on every lock path. */
  let session: UnlockSession | null = null;
  /** Prompt ids only ever go up, so an id is never reused, even across a lock. */
  let lastPrompt = 0;
  /** The open Face ID prompt and the controller that aborts it, until it is answered or closed. */
  let pending: { prompt: number; abort: AbortController } | null = null;
  const tokens = new ItemTokens();
  const initialRoute = parseHash(location.hash, tokens);
  const state: AppState = {
    route: initialRoute.name === "lock" ? { name: "home" } : initialRoute,
    history: [],
    phase: lock.phase,
    dataSource: source.kind,
    snapshot: null,
    loadError: null,
    scope: null,
    sort: "title",
    searchText: "",
    sheet: null,
    revealed: {},
    largeType: null,
    clipboard: null,
    toast: null,
    privacyCover: false,
    favoriteOverrides: {},
    pinned: [...(opts.pinned ?? [])],
    homePrefs: DEFAULT_HOME_PREFS,
    autoLock: 300,
    unlockedAt: null,
    unlockMethod: unlocker.method,
    online: navigator.onLine,
    version: opts.version,
    now: clock.now(),
  };

  const guard = new ClipboardGuard(port, clock, (s) => {
    state.clipboard = s;
    render();
  });

  const scroll = new Map<string, number>();
  let renderedKey = "";
  let toastTimer: ReturnType<typeof setTimeout> | undefined;
  let revealTimer: ReturnType<typeof setTimeout> | undefined;

  // ------------------------------------------------------------ state helpers
  const setLock = (next: LockState) => {
    const wasUnlocked = lock.phase === "unlocked";
    // A prompt closed by anything other than its own answer (a hide under "Immediately", a lock)
    // is aborted, so an unlocker that supports it can cancel the Face ID request.
    if (pending && next.prompt !== pending.prompt) {
      pending.abort.abort();
      pending = null;
    }
    lock = next;
    state.phase = next.phase;
    state.unlockedAt = next.unlockedAt;
    if (wasUnlocked && next.phase !== "unlocked") dropSecrets();
  };

  /**
   * Quick Search rule 14: every close path drops the app's references to vault values. It also
   * revokes the session, so an in-flight load is discarded when it lands, and retires the item
   * tokens, so no URL from this session opens an item in the next.
   */
  const dropSecrets = () => {
    revokeSession(session);
    session = null;
    tokens.clear();
    state.snapshot = null;
    state.loadError = null;
    state.revealed = {};
    state.largeType = null;
    state.sheet = null;
  };

  const syncUrl = () => history.replaceState(null, "", formatRoute(state.route, tokens));

  const toast = (message: string) => {
    state.toast = message;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      state.toast = null;
      render();
    }, TOAST_MS);
  };

  const scheduleRevealExpiry = () => {
    clearTimeout(revealTimer);
    const at = nextExpiry(state.revealed);
    if (at === null) return;
    revealTimer = setTimeout(() => {
      state.now = clock.now();
      state.revealed = pruneExpired(state.revealed, state.now);
      if (state.largeType && !(state.largeType in state.revealed)) state.largeType = null;
      render();
      scheduleRevealExpiry();
    }, Math.max(0, at - clock.now()) + 20);
  };

  /**
   * Calls the unlocker inside the tap (WebKit gesture rule). A throw or a rejection, which is how
   * an aborted WebAuthn request can end, counts as a failed prompt: the vault stays locked.
   */
  const askUnlocker = (signal: AbortSignal): Promise<UnlockResult> => {
    const failed: UnlockResult = { ok: false, reason: "failed" };
    try {
      return unlocker.unlock(signal).catch(() => failed);
    } catch {
      return Promise.resolve(failed);
    }
  };

  const findField = (ref: FieldRef) => {
    const { itemId, fieldId } = splitFieldRef(ref);
    const item = state.snapshot?.items.find((i) => i.id === itemId);
    return item?.fields.find((f) => f.id === fieldId) ?? null;
  };

  // ------------------------------------------------------------ actions
  const actions: AppActions = {
    navigate(route: Route) {
      state.history.push(state.route);
      state.route = route;
      state.sheet = null;
      syncUrl();
      render();
    },
    back() {
      state.route = state.history.pop() ?? parentOf(state.route);
      syncUrl();
      render();
    },
    openSheet(sheet: Sheet) {
      state.sheet = sheet;
      render();
    },
    closeSheet() {
      state.sheet = null;
      render();
    },
    setScope(wallet: WalletId | null) {
      state.scope = wallet;
      render();
    },
    setSort(sort: SortKey) {
      state.sort = sort;
      render();
    },
    setSearchText(text: string) {
      state.searchText = text;
      render();
    },
    toggleSearchWallet(wallet: WalletId) {
      const order = state.snapshot?.wallets.map((w) => w.id) ?? [];
      state.searchText = toggleWalletInText(state.searchText, wallet, order);
      render();
    },
    copyField(ref: FieldRef) {
      const field = findField(ref);
      if (!field) return;
      // writeText is called synchronously inside this tap's handler (WebKit gesture rule).
      void guard.copy(copyValue(field, clock.now()), field.label).then((ok) => {
        toast(ok ? `Copied ${field.label}` : "Couldn't copy");
        render();
      });
    },
    clearClipboard() {
      void guard.clear().then(() => render());
    },
    toggleReveal(ref: FieldRef) {
      state.now = clock.now();
      state.revealed = toggleReveal(state.revealed, ref, state.now);
      scheduleRevealExpiry();
      render();
    },
    showLargeType(ref: FieldRef) {
      state.now = clock.now();
      state.revealed = revealFor(state.revealed, ref, state.now);
      state.largeType = ref;
      scheduleRevealExpiry();
      render();
    },
    closeLargeType() {
      if (state.largeType) delete state.revealed[state.largeType];
      state.revealed = { ...state.revealed };
      state.largeType = null;
      render();
    },
    togglePin(ref: FieldRef) {
      state.pinned = state.pinned.includes(ref) ? state.pinned.filter((r) => r !== ref) : [...state.pinned, ref];
      toast(state.pinned.includes(ref) ? "Pinned to Home" : "Unpinned");
      render();
    },
    toggleFavorite(itemId: string) {
      const item = state.snapshot?.items.find((i) => i.id === itemId);
      if (!item) return;
      const now = !(state.favoriteOverrides[itemId] ?? item.favorite);
      state.favoriteOverrides = { ...state.favoriteOverrides, [itemId]: now };
      toast(now ? "Added to Favorites" : "Removed from Favorites");
      render();
    },
    setHomePrefs(prefs: HomePrefs) {
      state.homePrefs = prefs;
      render();
    },
    unlock() {
      const prompt = ++lastPrompt;
      const next = beginUnlock(lock, prompt);
      // A prompt is already open, or the vault is already unlocked: ask Face ID nothing.
      if (next.prompt !== prompt) return;
      setLock(next);
      const abort = new AbortController();
      pending = { prompt, abort };
      render();
      void askUnlocker(abort.signal).then(async (result) => {
        if (pending?.prompt === prompt) pending = null;
        // CSO gate on PR #100: an answer counts only for the prompt that asked for it. One for a
        // prompt that a hide, a lock or a newer prompt has closed changes nothing, and its
        // session is revoked unused.
        const forOpenPrompt = lock.phase === "prompting" && lock.prompt === prompt;
        setLock(finishUnlock(lock, prompt, result, clock.now()));
        if (!result.ok) return render();
        if (!forOpenPrompt) {
          revokeSession(result.session);
          return render();
        }
        const mine = result.session;
        session = mine;
        state.loadError = null;
        render();
        // The vault can lock while the load runs (Lock Now, auto-lock on return, "Immediately"
        // as the page hides), and a fresh unlock can start a newer load. Only a load for the
        // session that is still current is kept; anything else is discarded unread.
        const current = () => session === mine && lock.phase === "unlocked";
        try {
          const snapshot = await source.load(mine);
          if (!current()) return render();
          state.snapshot = snapshot;
          state.loadError = null;
        } catch (e) {
          if (!current()) return render();
          state.loadError = e instanceof Error ? e.message : String(e);
        }
        render();
      });
    },
    lock() {
      setLock(lockNow(lock));
      render();
    },
    setAutoLock(setting: AutoLockSetting) {
      state.autoLock = setting;
      render();
    },
  };

  // ------------------------------------------------------------ composition
  const SCREENS: Record<Route["name"], (s: AppState, a: AppActions) => HTMLElement> = {
    home: renderHome,
    items: renderItems,
    search: renderSearch,
    item: renderDetail,
    settings: renderSettings,
    lock: renderHome,
  };

  const renderSheet = (sheet: Sheet): HTMLElement => {
    switch (sheet.kind) {
      case "switcher":
        return renderSwitcherSheet(state, actions);
      case "search-scope":
        return renderSearchScopeSheet(state, actions);
      case "sort":
        return renderSortSheet(state, actions);
      case "categories":
        return renderCategoriesSheet(state, actions);
      case "field-menu":
        return renderFieldMenuSheet(state, actions, sheet.itemId, sheet.fieldId);
      case "item-menu":
        return renderItemMenuSheet(state, actions, sheet.itemId);
      case "customize-home":
        return renderCustomizeHomeSheet(state, actions);
      case "autolock":
        return renderAutoLockSheet(state, actions);
    }
  };

  const TABS: [TabId, string, string, Route][] = [
    ["home", "Home", "home", { name: "home" }],
    ["items", "Items", "items", { name: "items", list: "all" }],
    ["search", "Search", "search", { name: "search" }],
  ];

  const tabBar = () =>
    h(
      "nav",
      { class: "tabbar", "aria-label": "Tabs" },
      ...TABS.map(([id, label, glyph, route]) =>
        h(
          "button",
          {
            class: tabOf(state.route) === id ? "tab selected" : "tab",
            "aria-current": tabOf(state.route) === id ? "page" : undefined,
            onclick: () => {
              if (screenKey(state.route) === screenKey(route)) {
                scroll.set(screenKey(route), 0);
                root.querySelector(".screen")?.scrollTo({ top: 0, behavior: "smooth" });
                return;
              }
              state.history = [];
              state.route = route;
              state.sheet = null;
              syncUrl();
              render();
            },
          },
          icon(glyph),
          label,
        ),
      ),
    );

  function render(): void {
    state.now = clock.now();

    // Keep the focused input, its caret, and the scroll position across the rebuild.
    const active = document.activeElement as HTMLInputElement | null;
    const keep = active?.dataset?.keep;
    const oldValue = keep ? active!.value : "";
    const caret = keep ? active!.selectionStart ?? oldValue.length : 0;
    const oldScreen = root.querySelector<HTMLElement>(".screen");
    if (oldScreen && renderedKey) scroll.set(renderedKey, oldScreen.scrollTop);

    const layers: (Node | null)[] = [];
    if (state.phase !== "unlocked") {
      layers.push(renderLock(state, actions));
      renderedKey = "lock";
    } else if (!state.snapshot) {
      layers.push(
        h(
          "main",
          { class: "screen" },
          state.loadError ? emptyState("close", "Couldn't open this copy", state.loadError) : h("div", { class: "empty-state" }, h("div", { class: "skeleton" }), h("div", { class: "skeleton" }), h("div", { class: "skeleton" })),
        ),
      );
      renderedKey = "loading";
    } else {
      layers.push(SCREENS[state.route.name](state, actions), tabBar(), renderClipboardBar(state, actions), state.sheet ? renderSheet(state.sheet) : null, renderLargeType(state, actions));
      renderedKey = screenKey(state.route);
    }
    layers.push(renderToast(state), renderPrivacyCover(state));

    const app = h("div", { class: "app" }, ...layers);
    root.replaceChildren(app);

    const screen = root.querySelector<HTMLElement>(".screen");
    if (screen) screen.scrollTop = scroll.get(renderedKey) ?? 0;
    if (keep) {
      const next = root.querySelector<HTMLInputElement>(`[data-keep="${keep}"]`);
      if (next) {
        next.focus({ preventScroll: true });
        const at = next.value === oldValue ? caret : caretAfterRewrite(oldValue, next.value, caret);
        next.setSelectionRange(at, at);
      }
    }
  }

  // ------------------------------------------------------------ platform wiring
  // Every tap passes the clipboard guard first (capture phase), so the next tap clears a copied
  // value. Copy controls are skipped: their own copy overwrites the clipboard.
  root.addEventListener(
    "click",
    (e) => {
      void guard.onTap({ isCopyControl: isCopyControl(e.target as Element | null) });
    },
    { capture: true },
  );

  watchVisibility(document, window, () => document.visibilityState === "hidden", {
    hidden() {
      state.revealed = {};
      state.largeType = null;
      state.privacyCover = true;
      setLock(onHidden(lock, clock.now(), state.autoLock));
      render();
    },
    visible() {
      state.privacyCover = false;
      setLock(onVisible(lock, clock.now(), state.autoLock));
      render();
    },
  });

  window.addEventListener("hashchange", () => {
    const r = parseHash(location.hash, tokens);
    if (screenKey(r) === screenKey(state.route)) return;
    state.history = [];
    state.route = r.name === "lock" ? { name: "home" } : r;
    state.sheet = null;
    render();
  });
  window.addEventListener("online", () => ((state.online = true), render()));
  window.addEventListener("offline", () => ((state.online = false), render()));

  // One-second tick only where something on screen counts down (one-time codes).
  setInterval(() => {
    if (state.phase !== "unlocked" || !state.snapshot || state.privacyCover || state.sheet) return;
    const r = state.route.name;
    if (r === "item" || r === "home" || state.largeType) render();
  }, 1_000);

  render();
}
